/**
 * TimelineView コンポーネント
 *
 * タイムラインアイテムを時系列で表示するメインビューコンポーネント。
 */

import { useMemo } from 'react';
import { TimelineItemCard } from '@/components/atoms/TimelineItemCard';
import { ChronologyIcon } from '@/components/atoms/ChronologyIcon';
import { cn } from '@/lib/cn';
import type { TimelineItem, ChronologyEntry } from '@/types/domain';
import { useI18n } from '@/i18n/useI18n';

const EMPTY_CHRONOLOGY_ARRAY: ChronologyEntry[] = [];

/**
 * TimelineView コンポーネントのProps
 */
export interface TimelineViewProps {
  /** 表示するタイムラインアイテム */
  items: TimelineItem[];

  /** 年表エントリ */
  chronologyEntries?: ChronologyEntry[];

  /** 現在のフィルタ状態を含むURL（詳細ページから戻る用） */
  currentFilterUrl?: string;

  /** カスタムクラス */
  className?: string;

  /** ローディング状態 */
  isLoading?: boolean;

  /** エラーメッセージ */
  error?: string;
}

/**
 * アイテム配列を年ごとにグループ化するジェネリックヘルパー
 *
 * @param items - グループ化するアイテムの配列
 * @param getDate - アイテムから日付文字列を取得する関数
 * @returns 年をキーとした Map
 */
function groupByYear<T>(
  items: T[],
  getDate: (item: T) => string | undefined
): Map<number, T[]> {
  const grouped = new Map<number, T[]>();

  items.forEach((item) => {
    const date = getDate(item);
    if (!date) return;
    const year = parseInt(date.substring(0, 4), 10);
    if (!isNaN(year)) {
      const existing = grouped.get(year) ?? [];
      grouped.set(year, [...existing, item]);
    }
  });

  return grouped;
}

/**
 * 日付からMM/DD形式の文字列を抽出
 * "1964-09-21" → "09/21"
 * "1964" や "1964-09" → null
 */
function formatMonthDay(date: string): string | null {
  const parts = date.split('-');
  if (parts.length >= 3 && parts[1] && parts[2]) {
    return `${parts[1]}/${parts[2]}`;
  }
  return null;
}

/**
 * 年表エントリ表示コンポーネント
 */
function ChronologyEntryItem({ entry }: { entry: ChronologyEntry }) {
  const { t } = useI18n();
  const monthDay = formatMonthDay(entry.date);

  const content = (
    <div className="flex items-start gap-2">
      <ChronologyIcon category={entry.category} />
      {monthDay ? (
        <span className="text-sm font-medium text-muted-foreground/70 tabular-nums w-12 flex-shrink-0">
          {monthDay}
        </span>
      ) : null}
      <span className="text-sm text-muted-foreground">{entry.description}</span>
    </div>
  );

  if (entry.url) {
    return (
      <a
        href={entry.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block hover:bg-muted/50 rounded px-2 py-1 -mx-2 transition-colors"
      >
        {content}
        <span className="sr-only">{t('common.externalLink')}</span>
      </a>
    );
  }

  return <div className="px-2 py-1 -mx-2">{content}</div>;
}

/**
 * TimelineView コンポーネント
 *
 * 使用例:
 * ```tsx
 * <TimelineView items={filteredItems} chronologyEntries={entries} />
 * ```
 */
export function TimelineView({
  items,
  chronologyEntries = EMPTY_CHRONOLOGY_ARRAY,
  currentFilterUrl,
  className,
  isLoading = false,
  error,
}: TimelineViewProps) {
  const { t, language } = useI18n();

  // 年ごとにグループ化し、年の配列を取得（昇順ソート）
  const { itemsByYear, chronologyByYear, sortedYears } = useMemo(() => {
    const groupedItems = groupByYear(items, (item) => item.date);
    const groupedChronology = groupByYear(
      chronologyEntries,
      (entry) => entry.date
    );

    // 両方の年をマージしてソート
    const allYears = new Set<number>([
      ...groupedItems.keys(),
      ...groupedChronology.keys(),
    ]);
    const years = Array.from(allYears).sort((a, b) => a - b);

    return {
      itemsByYear: groupedItems,
      chronologyByYear: groupedChronology,
      sortedYears: years,
    };
  }, [items, chronologyEntries]);

  // ローディング状態
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center min-h-[400px]',
          className
        )}
      >
        <div className="text-center" role="status" aria-live="polite">
          <div className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <div
        className={cn(
          'flex items-center justify-center min-h-[400px]',
          className
        )}
      >
        <div className="text-center">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  // データがない場合
  if (items.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center min-h-[400px]',
          className
        )}
      >
        <div className="text-center">
          <p className="text-muted-foreground">{t('timeline.noItems')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative', className)} data-testid="timeline-container">
      {/* タイムラインアイテム */}
      <div className="space-y-8">
        {sortedYears.map((year, index) => {
          const yearItems = itemsByYear.get(year) || [];
          const yearChronology = chronologyByYear.get(year) || [];
          const isFirst = index === 0;
          const isLast = index === sortedYears.length - 1;
          const hasItems = yearItems.length > 0;

          return (
            <div
              key={year}
              className={cn(
                'relative flex gap-8',
                // 作品・イベントがある年は下に余白を追加
                hasItems && 'pb-16'
              )}
            >
              {/* タイムライン軸とマーカー */}
              <div className="relative flex-shrink-0">
                {/* 縦線（上半分） */}
                {!isFirst && (
                  <div className="absolute left-8 top-0 h-[50%] w-0.5 bg-border" />
                )}

                {/* マーカードット */}
                <div className="relative z-10 flex h-16 w-16 items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-primary" />
                </div>

                {/* 縦線（下半分） */}
                {!isLast && (
                  <div
                    className="absolute left-8 top-[50%] bottom-0 w-0.5 bg-border"
                    style={{
                      height: hasItems
                        ? 'calc(100% + 2rem)'
                        : 'calc(100% + 2rem)',
                    }}
                  />
                )}
              </div>

              {/* コンテンツ */}
              <div className="flex-1 space-y-4">
                {/* 年のヘッダー */}
                <div className="sticky top-0 bg-background/95 backdrop-blur z-10 py-2">
                  <h3
                    className="text-lg font-bold text-primary"
                    data-testid={`timeline-year-${year}`}
                  >
                    {year}
                  </h3>
                </div>

                {/* 年表エントリ（補足情報） */}
                {yearChronology.length > 0 && (
                  <div className="space-y-1 border-l-2 border-muted pl-4 mb-4">
                    {yearChronology.map((entry) => (
                      <ChronologyEntryItem key={entry.id} entry={entry} />
                    ))}
                  </div>
                )}

                {/* その年のアイテム */}
                {yearItems.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {yearItems.map((item) => (
                      <TimelineItemCard
                        key={item.id}
                        item={item}
                        language={language}
                        currentFilterUrl={currentFilterUrl}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* アイテム数の表示 */}
      <div className="mt-8 text-center text-sm text-muted-foreground">
        {t('timeline.itemCount', { count: items.length })}
      </div>
    </div>
  );
}
